import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  listAttendances,
  deleteattendance,
  getattendanceById,
} from "../../../../services/MarkattendancesService";
import {
  getTrainers,
  getTrainerByUserId,
} from "../../../../services/TrainerService";
import { getBatches } from "../../../../services/BatchService";
import { getMe } from "../../../../services/AuthService";
import { Download } from "lucide-react";
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import ExportDialog from "../../common/Export/ExportDialog";
import useScrollLoadMore from "../../common/useScrollLoadMore";
import AddAttendance from "./AddAttendance.jsx";
import { AttendancePie, initialsOf, avatarTone } from "./AttendanceBits.jsx";
// This page is part of the trainer area, so it reads that area's single
// stylesheet rather than keeping one of its own.
import "../TrainerDashboard.css";
import { useAuth } from "../../../../context/AuthContext";
import { toast } from '../../common/Toast/toast';
import NewCustomAsyncSelectDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
import debounce from "lodash/debounce";
import FilterSheet from "../../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../../common/useDebouncedCallback";
import { confirm as confirmDialog } from '../../common/Confirm/confirm';
import PopupDone, { usePopupDone } from '../../common/PopupDone/PopupDone';

// Splits the stored topics string into a list. Module scope because the
// Topics column counts them and the popup lists them, and the two must
// not disagree about what counts as a topic.
const formatTopics = (topicsString) => {
  if (
    !topicsString ||
    topicsString.trim() === "" ||
    topicsString === "No topics"
  ) {
    return [];
  }

  // Split by commas first, which handles the common "-2" trailing bug when syllabusId contains comma separated values
  const rawTopics = topicsString.split(/[\n\r,]+/);

  return rawTopics
    .map((topic) => {
      let cleaned = topic.trim();
      // Remove trailing "-2" or "-2-2..." patterns that might have been appended
      cleaned = cleaned.replace(/(?:-2)+$/, "");
      return cleaned;
    })
    .filter((topic) => topic !== "");
};

// Tooltip. react-bootstrap's <Tooltip> emits .tooltip / .tooltip-inner /
// .tooltip-arrow, which Bootstrap's stylesheet used to draw -- this project
// deleted it, so those rendered as bare text floating over the page. This
// one is drawn by ::after/::before in TrainerDashboard.css, costs no
// JavaScript, and cannot be left behind when a row unmounts.
const Tip = ({ label, children, className = "" }) => (
  <span className={`pg-tip ${className}`.trim()} data-tip={label}>
    {children}
  </span>
);

// Topics covered by one attendance session, opened from the Topics column.
// Styles live in the single TrainerDashboard.css -- none of its own.
const TopicsModal = ({ isOpen, onClose, topics, title, date }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const topicsArray = formatTopics(topics);

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pg-topics-title"
      onClick={onClose}
    >
      <div className="pg-modal-panel" onClick={(e) => e.stopPropagation()}>
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            <h3 className="pg-modal-title" id="pg-topics-title">
              Topics covered
            </h3>
            <p className="pg-modal-sub">
              {title || "Attendance topics"}
              {date ? ` · ${date}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {/* Label above the figure, per the page's type rule. */}
          <div className="pg-modal-count">
            <span className="pg-modal-count-label">
              Topic{topicsArray.length === 1 ? "" : "s"}
            </span>
            <b className="pg-modal-count-value">{topicsArray.length}</b>
            <span className="pg-modal-count-note">Class session coverage</span>
          </div>

          {topicsArray.length === 0 ? (
            <div className="pg-state">
              <p className="pg-state-title">No topics covered</p>
              <p className="pg-state-text">
                No topics were recorded against this attendance session.
              </p>
            </div>
          ) : (
            <ol className="pg-topiclist">
              {topicsArray.map((topic, index) => (
                <li className="pg-topiclist-row" key={`${topic}-${index}`}>
                  <span className="pg-topiclist-num">{index + 1}</span>
                  <span className="pg-topiclist-text">{topic}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <footer className="pg-modal-foot">
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

// Batch names are clipped in the list at this many characters.
const BATCH_NAME_MAX = 25;

// The attendance record in full. Was a SweetAlert HTML string driven by
// window.* callbacks; it is a component so the student list can be
// searched and the chart drawn without going through innerHTML.
const AttendanceDetailModal = ({ detail, done, onClose, onEdit, onDelete, onExport }) => {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  // The query is cleared per record, not per render.
  useEffect(() => {
    setQuery("");
  }, [detail?.id]);

  if (!detail) return null;

  const {
    batchName,
    trainerName,
    courseName,
    dateLabel,
    topics,
    students,
    presentCount,
    absentCount,
    canEdit,
    editLockReason,
    canDelete,
  } = detail;

  const q = query.trim().toLowerCase();
  const shown = q
    ? students.filter((s) =>
        [s.name, s.email, s.phone].some((v) =>
          String(v || "").toLowerCase().includes(q),
        ),
      )
    : students;

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pgm-detail-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The batch is what the record is about, so it is the title --
            in full, never clipped the way the list column clips it. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="pgm-detail-title">
              {batchName}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Trainer</dt>
                <dd>{trainerName}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Course</dt>
                <dd>{courseName}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Date</dt>
                <dd>{dateLabel}</dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
          <>
          {/* Why Edit is not in the footer, said in the server's own
              words, at the top where it is read before the record is. */}
          {editLockReason && (
            <p className="att-note att-note--warn" role="status">
              {editLockReason}
            </p>
          )}

          {/* Topics first: what the session covered, before how many
              turned up to it. */}
          <section className="pgm-section">
            <h3 className="pgm-section-title">
              Topics covered
              <span className="pgm-section-count">{topics.length}</span>
            </h3>

            {topics.length === 0 ? (
              <p className="pgm-empty">No topics were recorded for this session.</p>
            ) : (
              <table className="pgm-topics-table">
                <thead>
                  <tr>
                    <th className="pgm-num">#</th>
                    <th>Topic</th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic, i) => (
                    <tr key={`${topic}-${i}`}>
                      <td className="pgm-num">{i + 1}</td>
                      <td>{topic}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="pgm-section">
            <h3 className="pgm-section-title">Attendance</h3>
            <AttendancePie present={presentCount} absent={absentCount} />
          </section>

          <section className="pgm-section">
            <h3 className="pgm-section-title">
              Students
              <span className="pgm-section-count">{students.length}</span>
            </h3>

            <input
              type="search"
              className="pg-input pgm-search"
              placeholder="Search by name, email or mobile..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search students"
            />

            {shown.length === 0 ? (
              <p className="pgm-empty">No student matches &ldquo;{query}&rdquo;.</p>
            ) : (
              <table className="pgm-table pgm-students">
                <thead>
                  <tr>
                    <th className="pgm-num">#</th>
                    <th>Student</th>
                    <th>Mobile</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((s, i) => (
                    <tr key={s.id || `${s.name}-${i}`}>
                      <td className="pgm-num">{i + 1}</td>
                      <td>
                        <div className="pgm-person">
                          <span
                            className={`pgm-avatar pgm-avatar--${avatarTone(s.name)}`}
                            aria-hidden="true"
                          >
                            {initialsOf(s.name)}
                          </span>
                          <span className="pgm-person-id">
                            <b className="pgm-person-name">{s.name || "—"}</b>
                            <span className="pgm-person-mail">
                              {s.email || "No email"}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="pgm-tel">{s.phone || "—"}</td>
                      <td>
                        <span
                          className={`pgm-pill ${
                            s.status === "present"
                              ? "pgm-pill--good"
                              : "pgm-pill--bad"
                          }`}
                        >
                          {s.status === "present" ? "Present" : "Absent"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end --
            you read the record, then act on it. Close stays last and is
            the only filled button, so the destructive one is never the
            default thing under the cursor. */}
        {!done && (
        <footer className="pg-modal-foot">
          <div className="pgm-actions">
            <button type="button" className="pgm-btn" onClick={onExport}>
              Export CSV
            </button>
            {canEdit && (
              <button type="button" className="pgm-btn" onClick={onEdit}>
                Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="pgm-btn pgm-btn--danger"
                onClick={onDelete}
              >
                Delete
              </button>
            )}
          </div>
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </footer>
        )}
      </div>
    </div>
  );
};

const ListAttendance = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("all");
  // What is typed into the filter section but not yet applied.
  const [searchDraft, setSearchDraft] = useState("");
  const [searchFieldDraft, setSearchFieldDraft] = useState("all");
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // 100 a batch. The list scrolls rather than paging, so this is how much
  // arrives per fetch, not how much the reader has to click through.
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  // Fetching the next scroll batch. Kept apart from `loading` because
  // that one draws a veil over the whole table -- right for a fresh load,
  // wrong for adding rows to the bottom of a list being read.
  const [loadingMore, setLoadingMore] = useState(false);
  const [isTrainer, setIsTrainer] = useState(false);
  const [actualTrainerId, setActualTrainerId] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedTrainerFilter, setSelectedTrainerFilter] = useState("");
  const [selectedBatchFilter, setSelectedBatchFilter] = useState("");
  const [appliedTrainerFilter, setAppliedTrainerFilter] = useState("");
  const [appliedBatchFilter, setAppliedBatchFilter] = useState("");

  // Dropdown options with pagination
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);

  // Pagination state for dropdowns
  const [trainerPage, setTrainerPage] = useState(1);
  const [batchPage, setBatchPage] = useState(1);
  const [trainerHasMore, setTrainerHasMore] = useState(true);
  const [batchHasMore, setBatchHasMore] = useState(true);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [trainerSearch, setTrainerSearch] = useState("");
  const [batchSearch, setBatchSearch] = useState("");

  // Selected dropdown values
  // Trainer filter is multi-select — an array of options, not a single one.
  const [selectedTrainer, setSelectedTrainer] = useState([]);
  // Batch filter is multi-select — an array of options, not a single one.
  const [selectedBatch, setSelectedBatch] = useState([]);
  // Batch status toggle for the "Filter by Batch" dropdown: "" (all),
  // "active", or "ended" — same convention as the Batch Report page.
  const [batchStatusFilter, setBatchStatusFilter] = useState("");

  // The record shown in the detail modal, or null when it is closed.
  const [detail, setDetail] = useState(null);

  // The mark/edit form, shown in a dialog rather than on its own route.
  // null = closed; { recordId } = editing; { recordId: null } = adding.
  const [formModal, setFormModal] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  /* The single record's roster export, raised from the view popup.
     null = closed. Its own state rather than exportOpen's, because the
     two carry different rows, columns and filename. */
  const [recordExport, setRecordExport] = useState(null);
  // The question the form's current step is asking, so the dialog's own
  // title says it rather than the step repeating it inside the body.
  const [formStep, setFormStep] = useState(null);
  // A save in the form popup, or a delete from the view popup, is confirmed
  // in that popup, which closes itself a few seconds later.
  const { done: formDone } = usePopupDone(Boolean(formModal));
  // Not while the record's Export dialog sits over it: that dialog's
  // "rows exported" receipt is a toast, not a change to this record.
  const { done: detailDone } = usePopupDone(Boolean(detail) && !recordExport);

  const [topicsModal, setTopicsModal] = useState({
    isOpen: false,
    topics: "",
    title: "",
    date: "",
  });

  const filterDropdownPageSize = 20;
  const navigate = useNavigate();
  const hasLoaded = useRef(false);
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  const isFetching = useRef(false);
  // Page 1 replaces the list; every page after it appends. Held in a ref
  // because the fetch reads it after an await, when a re-render may
  // already have moved on.
  const appendRef = useRef(false);
  const listRef = useRef(null);
  // The foot under the list. It is what the observer watches, and it
  // sits in the normal flow of the page -- no pane of its own.
  const listEndRef = useRef(null);
  // Bumped on every fetchTrainers/fetchBatches call so a slow, now-stale
  // request (e.g. fired before the user finished picking a second trainer)
  // can't resolve late and clobber a fresher one's results — only the
  // response whose id still matches the latest is applied.
  const trainerFetchSeqRef = useRef(0);
  const batchFetchSeqRef = useRef(0);

  useEffect(() => {
    if (!hasPermission("list-attendance", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to add attendance.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  const showTopicsModal = (topics, batchName, date) => {
    setTopicsModal({
      isOpen: true,
      topics: topics || "",
      title: batchName || "Batch Topics",
      date: date || "",
    });
  };

  const closeTopicsModal = () => {
    setTopicsModal({
      isOpen: false,
      topics: "",
      title: "",
      date: "",
    });
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await getMe();
      const userData = response.data;
      setCurrentUser(userData);

      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminUser =
        profileLabel === "admin" ||
        userData.isSuperAdmin ||
        profileLabel === "crm";

      setIsTrainer(isTrainerUser);
      setIsAdmin(isAdminUser);
  let resolvedTrainerId = null;
    if (isTrainerUser) {
      const userId = userData.id || userData._id || userData.userId;
      try {
        const trainerResponse = await getTrainerByUserId(userId);
        if (trainerResponse && trainerResponse.data) {
          resolvedTrainerId = trainerResponse.data.id || trainerResponse.data._id;
          setActualTrainerId(resolvedTrainerId);
        } else {
          resolvedTrainerId = userId;
          setActualTrainerId(userId);
        }
      } catch (error) {
        console.error("Error fetching trainer data:", error);
        resolvedTrainerId = userId;
        setActualTrainerId(userId);
      }
    }

    return { ...userData, actualTrainerId: resolvedTrainerId };
    } catch (error) {
      console.error("Error fetching current user", error);
      setCurrentUser(null);
      return null;
    }
  };

  // Fetch trainers with pagination and search
  const fetchTrainers = useCallback(
    async (page = 1, search = "", append = false) => {
      const requestId = ++trainerFetchSeqRef.current;
      setTrainerLoading(true);

      try {
        const limit = filterDropdownPageSize;
        const resp = await getTrainers(limit, page, search || "", false);
        const trainersData = resp?.data || [];

        const options = trainersData
          .map((trainer) => ({
            value: String(trainer.id || "").trim(),
            label: `${trainer.trainerName || trainer.name || "Unknown"} | ${trainer.trainerEmail || trainer.email || "No Email"}`,
          }))
          .filter((opt) => opt.value);

        // A newer fetchTrainers call has started since this one went out —
        // drop this (now stale) response instead of overwriting fresher data.
        if (requestId !== trainerFetchSeqRef.current) return options;

        if (append) {
          setTrainerOptions((prev) => [...prev, ...options]);
        } else {
          setTrainerOptions(options);
        }

        setTrainerHasMore(resp?.pagination?.hasNext || false);
        setTrainerPage(page);
        return options;
      } catch (error) {
        console.error("Error fetching trainers:", error);
        return [];
      } finally {
        if (requestId === trainerFetchSeqRef.current) setTrainerLoading(false);
      }
    },
    [filterDropdownPageSize],
  );

  // Fetch batches with pagination and search. `status` and `trainerOverride`
  // default to whatever the Active/Ended toggle and trainer selection
  // currently are, but a caller that just changed one of those (where the
  // state update hasn't re-rendered yet) can pass the new value explicitly
  // to avoid acting on a stale closure.
  const fetchBatches = useCallback(
    async (
      page = 1,
      search = "",
      append = false,
      status = batchStatusFilter,
      trainerOverride,
    ) => {
      const requestId = ++batchFetchSeqRef.current;
      setBatchLoading(true);

      try {
        const limit = filterDropdownPageSize;
        const trainerKey = isAdmin
          ? (trainerOverride !== undefined ? trainerOverride : selectedTrainerFilter) || undefined
          : isTrainer && currentUser?.id
            ? String(currentUser.id)
            : undefined;

        const resp = await getBatches(
          limit,
          page,
          search || "",
          "name",
          trainerKey,
          undefined,
          status || undefined,
        );
        const batchesData = resp?.data || [];

        const options = batchesData
          .map((batch) => ({
            value: String(batch.id || batch._id || "").trim(),
            label: batch.name || "Unknown Batch",
          }))
          .filter((opt) => opt.value);

        // A newer fetchBatches call (e.g. triggered by picking another
        // trainer, or the Active/Ended toggle) has started since this one
        // went out — drop this now-stale response instead of letting it
        // clobber fresher results with an outdated trainer/status filter.
        if (requestId !== batchFetchSeqRef.current) return options;

        if (append) {
          setBatchOptions((prev) => [...prev, ...options]);
        } else {
          setBatchOptions(options);
        }

        setBatchHasMore(resp?.pagination?.hasNext || false);
        setBatchPage(page);
        return options;
      } catch (error) {
        console.error("Error fetching batches:", error);
        return [];
      } finally {
        if (requestId === batchFetchSeqRef.current) setBatchLoading(false);
      }
    },
    [
      filterDropdownPageSize,
      isAdmin,
      isTrainer,
      currentUser?.id,
      selectedTrainerFilter,
      batchStatusFilter,
    ],
  );

  const fetchAttendanceRecords = async (user) => {
    if (isFetching.current) return;
    isFetching.current = true;
    // Captured before the request: the overlay must never appear for a
    // scroll batch, and reading the live ref after an await could get
    // the wrong answer if anything reset it meanwhile.
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);

    try {
      const profileLabel = user?.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminOrCrmUser =
        profileLabel === "admin" ||
        user?.isSuperAdmin ||
        profileLabel === "crm";

    const trainerIdParam =
  user && !user.isSuperAdmin && !isAdminOrCrmUser && isTrainerUser
    ? user.actualTrainerId || actualTrainerId || String(user.id)
    : appliedTrainerFilter
      ? String(appliedTrainerFilter)
      : undefined;
      const batchIdParam = appliedBatchFilter
        ? String(appliedBatchFilter)
        : undefined;

      const res = await listAttendances({
        limit: recordsPerPage,
        page: currentPage,
        search: searchTerm,
        // Sent even when it is "all": the endpoint resolves batchName /
        // courseName / trainerName to ids itself, and "all" is its own
        // documented value for searching everything.
        ...(searchField ? { searchField } : {}),
        ...(trainerIdParam ? { trainerId: trainerIdParam } : {}),
        ...(batchIdParam ? { batchId: batchIdParam } : {}),
      });

      const pageData = res?.data || [];
      const pagination = res?.pagination || {};
      setTotalRecords(Number(pagination.total || 0));

      // ================================
      // EXISTING MAPPING LOGIC (UNCHANGED)
      // ================================
      const getUserIds = (v) => {
        if (Array.isArray(v)) {
          return v
            .map((u) => (u && typeof u === "object" ? u.id : u))
            .map((x) => String(x || "").trim())
            .filter(Boolean);
        }
        if (typeof v === "string") {
          const s = v.trim();
          if (!s || s.toLowerCase() === "no students present") return [];
          return s
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
        }
        return [];
      };
      let attendanceList = pageData.map((item, index) => {
        let batch = null;

        if (Array.isArray(item.batchId) && item.batchId[0]) {
          batch = item.batchId[0];
        } else if (item.batchId && typeof item.batchId === "object") {
          batch = item.batchId;
        }

        const batchName = batch ? batch.name : "Unknown Batch";

        // Course name: the API's, printed as sent.
        const courseName = item.courseName || "N/A";

        // Trainer name
        let trainerName = "N/A";
        if (Array.isArray(item.trainerId) && item.trainerId[0]) {
          trainerName =
            item.trainerId[0].trainerName || item.trainerId[0].name || "N/A";
        } else if (item.trainerId && typeof item.trainerId === "object") {
          trainerName =
            item.trainerId.trainerName || item.trainerId.name || "N/A";
        }

        // Clean topics string (remove trailing -2 sequences)
        let cleanedTopics = item.syllabusId || "No topics";
        if (cleanedTopics !== "No topics") {
          cleanedTopics = cleanedTopics
            .split(",")
            .map((t) => t.trim().replace(/(?:-2)+$/, ""))
            .filter(Boolean)
            .join(", ");
        }

        return {
          ...item,
          batchName,
          trainerName,
          courseName,
          topics: cleanedTopics,
          presentUserIds: getUserIds(item.userIds),
        };
      });

      // Page 1 is a fresh list; the pages the scroll asks for extend it.
      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setAttendanceRecords((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...(attendanceList || []).filter((r) => !seen.has(r.id))];
        });
      } else {
        setAttendanceRecords(attendanceList || []);
      }
      appendRef.current = false;
    } catch (err) {
      console.error("Error fetching attendance records:", err);
      toast.error("Failed to fetch attendance records");
      if (!isAppend) setAttendanceRecords([]);
      appendRef.current = false;
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
  };

  // Export individual attendance record to CSV
  const handleExportRecord = async (record) => {
    try {
      const response = await getattendanceById(record.id);
      const attendanceData = response.data;

      let batch = null;
      if (Array.isArray(attendanceData.batchId) && attendanceData.batchId[0]) {
        batch = attendanceData.batchId[0];
      } else if (
        attendanceData.batchId &&
        typeof attendanceData.batchId === "object"
      ) {
        batch = attendanceData.batchId;
      }
      const batchName = batch ? batch.name : "Unknown Batch";

      // Course name: the API's, printed as sent.
      const courseName = attendanceData.courseName || "N/A";

      // Get trainer name
      let trainerName = "N/A";
      if (
        Array.isArray(attendanceData.trainerId) &&
        attendanceData.trainerId[0]
      ) {
        trainerName =
          attendanceData.trainerId[0].trainerName ||
          attendanceData.trainerId[0].name ||
          "N/A";
      } else if (
        attendanceData.trainerId &&
        typeof attendanceData.trainerId === "object"
      ) {
        trainerName =
          attendanceData.trainerId.trainerName ||
          attendanceData.trainerId.name ||
          "N/A";
      }

      const rosterExport =
        Array.isArray(attendanceData.userIds) &&
        attendanceData.userIds.length > 0 &&
        attendanceData.userIds.every(
          (u) =>
            u &&
            typeof u === "object" &&
            Object.prototype.hasOwnProperty.call(u, "isPresent") &&
            typeof u.isPresent === "boolean",
        );

      let attendanceArray;
      if (rosterExport) {
        const byName = [...attendanceData.userIds].sort((a, b) =>
          String(a?.name || "")
            .trim()
            .localeCompare(String(b?.name || "").trim(), "en", {
              sensitivity: "base",
            }),
        );
        attendanceArray = byName.map((u, index) => ({
          "Sr. No": index + 1,
          "Student ID": String(u.id || u._id || ""),
          "Student Name": u.name || u.studentName || "Unknown Student",
          Email: u.email || u.studentEmail || "N/A",
          Phone: u.phone || u.mobileNumber || u.mobile || "N/A",
          Status: u.isPresent === true ? "Present" : "Absent",
        }));
      } else {
        const studentMap = {};

        if (batch?.students && Array.isArray(batch.students)) {
          batch.students.forEach((user) => {
            if (user && typeof user === "object") {
              const sid = String(user.id || user._id);
              studentMap[sid] = {
                name: user.studentName || user.name || "Unknown Student",
                email: user.studentEmail || user.email || "N/A",
                phone: user.phone || user.mobileNumber || user.mobile || "N/A",
              };
            }
          });
        } else if (batch?.userIds && Array.isArray(batch.userIds)) {
          batch.userIds.forEach((user) => {
            if (user && typeof user === "object") {
              const sid = String(user.id || user._id);
              studentMap[sid] = {
                name: user.studentName || user.name || "Unknown Student",
                email: user.studentEmail || user.email || "N/A",
                phone: user.phone || user.mobileNumber || user.mobile || "N/A",
              };
            }
          });
        }

        if (Array.isArray(attendanceData.userIds)) {
          attendanceData.userIds.forEach((user) => {
            if (
              user &&
              typeof user === "object" &&
              typeof user.isPresent !== "boolean"
            ) {
              const sid = String(user.id || user._id);
              studentMap[sid] = {
                name: user.studentName || user.name || "Unknown Student",
                email: user.studentEmail || user.email || "N/A",
                phone: user.phone || user.mobileNumber || user.mobile || "N/A",
              };
            }
          });
        }

        const presentUserIds = attendanceData.userIds
          ? Array.isArray(attendanceData.userIds)
            ? attendanceData.userIds.map((u) =>
                typeof u === "string" ? u.trim() : String(u.id || "").trim(),
              )
            : attendanceData.userIds
                .split(",")
                .map((id) => id.trim())
                .filter((id) => id)
          : [];
        const presentIdSetExport = new Set(presentUserIds.filter(Boolean));

        let allBatchUserIds = [];
        if (batch?.userId) {
          allBatchUserIds = batch.userId
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
        } else if (batch?.students && Array.isArray(batch.students)) {
          allBatchUserIds = batch.students
            .map((s) => (typeof s === "string" ? s : s.id || s._id))
            .filter(Boolean);
        } else if (batch?.userIds && Array.isArray(batch.userIds)) {
          allBatchUserIds = batch.userIds
            .map((id) => (typeof id === "string" ? id : id.id || id._id))
            .filter(Boolean);
        }

        attendanceArray = allBatchUserIds
          .map((userId, index) => {
            const uid = String(userId || "").trim();
            const sm = studentMap[userId] || studentMap[uid];
            return {
              "Sr. No": index + 1,
              "Student ID": uid,
              "Student Name": sm?.name || "Unknown Student",
              Email: sm?.email || "N/A",
              Phone: sm?.phone || "N/A",
              Status: presentIdSetExport.has(uid) ? "Present" : "Absent",
            };
          })
          .sort((a, b) =>
            (a["Student Name"] || "").localeCompare(
              b["Student Name"] || "",
              "en",
              {
                sensitivity: "base",
              },
            ),
          )
          .map((item, index) => ({
            ...item,
            "Sr. No": index + 1,
          }));
      }

      /* Hand off to the house dialog rather than writing the file here.
         What this replaced was a SEVENTH copy of the CSV writer, and it
         had the faults the shared one exists to fix: it quoted with
         `"${value}"` without doubling the quotes inside, so a name
         carrying one broke the row; it never quoted a line break, so a
         topic with a newline shifted every column after it; it wrote no
         BOM, so Excel mangled any non-ASCII name; and it guarded no
         leading =, +, - or @, so a cell could be read as a formula.

         It also asked nothing before writing: no row count, no column
         picker, no CSV-or-Excel, and no filename shown. The dialog is
         the confirm step; the toast it raises afterwards is the receipt,
         so the success toast goes with the writer. */
      const dateLabel = new Date(attendanceData.date).toLocaleDateString();
      setRecordExport({
        rows: attendanceArray,
        // Whole LINES above the table, not columns -- the same shape
        // /students-report uses for its report header.
        preamble: [
          `Attendance Report - ${dateLabel}`,
          `Batch: ${batchName}`,
          `Course: ${courseName}`,
          `Trainer: ${trainerName}`,
        ],
        filenameBase: `attendance-${batchName.replace(/\s+/g, "-").toLowerCase()}-${
          new Date(attendanceData.date).toISOString().split("T")[0]
        }`,
        scopeNote: `Every student on ${batchName} for this session, present and absent.`,
      });
    } catch (error) {
      console.error("Error exporting attendance data:", error);
      toast.error("Failed to export attendance data");
    }
  };

  /* The roster's columns. The rows are already shaped as plain objects
     above, so each column reads its own key -- the one shape the dialog
     needs to do everything else it does. */
  const recordExportColumns = [
    { key: "Sr. No", label: "Sr. No", value: (_row, i) => i + 1 },
    { key: "Student ID", label: "Student ID", value: (r) => r["Student ID"] },
    { key: "Student Name", label: "Student Name", value: (r) => r["Student Name"] },
    { key: "Email", label: "Email", value: (r) => r.Email },
    { key: "Phone", label: "Phone", value: (r) => r.Phone },
    { key: "Status", label: "Status", value: (r) => r.Status },
  ];

  // Get the currently filtered data based on search
  const getFilteredData = () => {
    if (!searchTerm || searchTerm.trim() === "") {
      return attendanceRecords;
    }

    const searchLower = searchTerm.toLowerCase();
    return attendanceRecords.filter((record) => {
      return (
        record.batchName?.toLowerCase().includes(searchLower) ||
        record.courseName?.toLowerCase().includes(searchLower) ||
        record.trainerName?.toLowerCase().includes(searchLower) ||
        record.topics?.toLowerCase().includes(searchLower) ||
        new Date(record.date)
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
          .toLowerCase()
          .includes(searchLower)
      );
    });
  };

  /*
   * How many people are on the batch behind a record. Written once here
   * because the export and the popup's own figures both need it and had
   * drifted: three shapes carry the roster depending on which endpoint
   * filled the record in.
   */
  const rosterSizeOf = (record) => {
    let batch = null;
    if (Array.isArray(record.batchId) && record.batchId[0]) {
      batch = record.batchId[0];
    } else if (record.batchId && typeof record.batchId === "object") {
      batch = record.batchId;
    }
    if (!batch) return 0;
    if (batch.userId) {
      return batch.userId.split(",").filter((id) => id.trim()).length;
    }
    if (Array.isArray(batch.students)) return batch.students.length;
    if (Array.isArray(batch.userIds)) return batch.userIds.length;
    return 0;
  };

  const presentCountOf = (record) =>
    record.presentUserIds ? record.presentUserIds.length : 0;

  /*
   * The summary columns, for the shared export popup. A dash stands in
   * for a zero on the three counts, as it did before -- a batch whose
   * roster never loaded reads as unknown, not as nobody.
   */
  const summaryExportColumns = [
    { key: "sr", label: "Sr. No", value: (_row, i) => i + 1 },
    {
      key: "date",
      label: "Date",
      value: (r) =>
        new Date(r.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
    },
    { key: "batchName", label: "Batch Name", value: (r) => r.batchName },
    { key: "courseName", label: "Course Name", value: (r) => r.courseName },
    { key: "trainerName", label: "Trainer Name", value: (r) => r.trainerName },
    {
      key: "topics",
      label: "Topics",
      value: (r) =>
        r.topics && r.topics !== "No topics"
          ? r.topics.replace(/[\r\n]+/g, " | ")
          : "No topics",
    },
    {
      key: "present",
      label: "Present Students",
      value: (r) => presentCountOf(r) || "-",
    },
    {
      key: "absent",
      label: "Absent Students",
      value: (r) => {
        const absent = Math.max(0, rosterSizeOf(r) - presentCountOf(r));
        return absent > 0 ? absent : "-";
      },
    },
    {
      key: "total",
      label: "Total Students",
      value: (r) => rosterSizeOf(r) || "-",
    },
  ];

  // Handle rows per page change
  // Only sets how big the NEXT batch is. The rows already loaded stay
  // exactly as they are -- no reset, no refetch. loadMore works out which
  // page the new size corresponds to from how many rows are on screen.
  const handleRowsPerPageChange = useCallback((newLimit) => {
    setRecordsPerPage(Number(newLimit));
  }, []);

  // The scroll asked for the next batch. Only ever moves forward, and
  // only while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loading || loadingMore) return;
    if (attendanceRecords.length >= totalRecords) return;
    appendRef.current = true;
    // Derived from the rows already on screen rather than counted up, so
    // the batch size can change mid-list without asking for a page that
    // has already been read. 100 loaded at a size of 25 is page 5, not
    // page 2. Any overlap a ragged division causes is dropped by the id
    // guard where the rows are appended.
    const nextPage = Math.floor(attendanceRecords.length / recordsPerPage) + 1;
    setCurrentPage(nextPage);
  }, [
    loading,
    loadingMore,
    attendanceRecords.length,
    totalRecords,
    recordsPerPage,
  ]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore]);

  // Trainer filter handler — multi-select, so `selectedOptions` is the full
  // list of chosen trainers, not just the first one. Sent to the backend as
  // a comma-separated list of ids (same CSV convention used elsewhere in
  // this app), which attendance.service.js's `list` turns into a Prisma
  // `{ in: [...] }` match.
  const handleTrainerFilterSelect = async (selectedOptions) => {
    const options = Array.isArray(selectedOptions) ? selectedOptions : [];
    const trainerCsv = options.map((o) => o.value).join(",");
    setSelectedTrainer(options);
    setSelectedTrainerFilter(trainerCsv);
    // Clear batch filter when trainer selection changes
    setSelectedBatch([]);
    setSelectedBatchFilter("");
    setBatchOptions([]);
    setBatchPage(1);
    setBatchHasMore(true);
    setBatchSearch("");
    // Refresh the batch dropdown's options for the new trainer selection
    // right away — covers the case where it's already open, instead of
    // waiting for the next time it's opened. Passed explicitly (not read
    // from state) since the setSelectedTrainerFilter above hasn't applied
    // to this closure yet.
    await fetchBatches(1, "", false, batchStatusFilter, trainerCsv);
  };

  // Batch status toggle (All / Active / Ended) — reloads the batch dropdown
  // options from scratch under the new status filter.
  const handleBatchStatusChange = async (value) => {
    setBatchStatusFilter(value);
    setSelectedBatch([]);
    setSelectedBatchFilter("");
    setBatchOptions([]);
    setBatchPage(1);
    setBatchHasMore(true);
    setBatchSearch("");
    await fetchBatches(1, "", false, value);
  };

  // Batch filter handler — multi-select, so `selectedOptions` is the full
  // list of chosen batches, not just the first one. Sent to the backend as
  // a comma-separated list of ids (same CSV convention used elsewhere in
  // this app), which attendance.service.js's `list` turns into a Prisma
  // `{ in: [...] }` match.
  const handleBatchFilterSelect = (selectedOptions) => {
    const options = Array.isArray(selectedOptions) ? selectedOptions : [];
    setSelectedBatch(options);
    setSelectedBatchFilter(options.map((o) => o.value).join(","));
  };

  /* The search box does not wait for Apply. It runs itself once the
     typing stops -- the pickers beside it still wait, because
     choosing three of those should cost one refetch, not three. */
  const runSearch = useDebouncedCallback((term, field) => {
    appendRef.current = false;
    setSearchTerm(term.trim());
    setSearchField(field);
    setCurrentPage(1);
  });

  const handleApplyFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setAppliedTrainerFilter(selectedTrainerFilter);
    setAppliedBatchFilter(selectedBatchFilter);
    setSearchTerm(searchDraft.trim());
    setSearchField(searchFieldDraft);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchDraft("");
    setSearchFieldDraft("all");
    setSearchTerm("");
    setSearchField("all");
    setSelectedTrainer([]);
    setSelectedBatch([]);
    setSelectedTrainerFilter("");
    setSelectedBatchFilter("");
    setAppliedTrainerFilter("");
    setAppliedBatchFilter("");
    setBatchStatusFilter("");
    setCurrentPage(1);
    setTrainerOptions([]);
    setBatchOptions([]);
    setTrainerPage(1);
    setBatchPage(1);
    setTrainerHasMore(true);
    setBatchHasMore(true);
    setTrainerSearch("");
    setBatchSearch("");
  };

  // Refresh handlers for dropdowns
  const handleTrainerRefresh = async () => {
    setTrainerSearch("");
    setTrainerPage(1);
    setTrainerHasMore(true);
    await fetchTrainers(1, "", false);
  };

  const handleBatchRefresh = async () => {
    setBatchSearch("");
    setBatchPage(1);
    setBatchHasMore(true);
    await fetchBatches(1, "", false);
  };

  useEffect(() => {
    const loadAllData = async () => {
      try {
        const user = await fetchCurrentUser();
        await fetchAttendanceRecords(user);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        toast.error("Failed to load dashboard data");
      }
    };

    if (!hasLoaded.current) {
      loadAllData();
      hasLoaded.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchAttendanceRecords(currentUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // recordsPerPage is deliberately absent: changing the batch size
    // must not refetch, or the list the reader is part-way through is
    // thrown away and rebuilt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedTrainerFilter,
    appliedBatchFilter,
    currentPage,
    searchTerm,
    searchField,
  ]);

  const handleDelete = async (recordId) => {
    const confirm = await confirmDialog({
      title: "Delete Confirmation",
      text: "Are you sure you want to delete this attendance record?",
      confirmButtonText: "Yes, Delete it!",
      tone: 'danger',
    });

    if (confirm.isConfirmed) {
      setLoading(true);
      try {
        await deleteattendance(recordId);
        /* The QUESTION was already the house confirm; the ANSWER shows in
           the view popup, in place of the record it removed, and the popup
           closes itself a few seconds later. */
        toast.success("Attendance record deleted");
        await fetchAttendanceRecords(currentUser);
      } catch (err) {
        console.error("Error deleting attendance record:", err);
        toast.error("Could not delete the attendance record. Try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (recordId) => {
    setFormModal({ recordId });
  };

  const handleView = async (recordId) => {
    try {
      const response = await getattendanceById(recordId);
      const record = response.data;

      // Ensure we get the correct batch details, falling back to embedded objects from API if they exist
      let batch = null;
      if (Array.isArray(record.batchId) && record.batchId[0]) {
        batch = record.batchId[0];
      } else if (record.batchId && typeof record.batchId === "object") {
        batch = record.batchId;
      }

      const batchName = batch ? batch.name : "Unknown Batch";

      // Course name: the API's, printed as sent.
      const courseName = record.courseName || "N/A";

      // Get trainer name from trainers data instead of API call
      let trainerName = "Not assigned";
      if (Array.isArray(record.trainerId) && record.trainerId[0]) {
        trainerName =
          record.trainerId[0].trainerName ||
          record.trainerId[0].name ||
          "Not assigned";
      } else if (record.trainerId && typeof record.trainerId === "object") {
        trainerName =
          record.trainerId.trainerName ||
          record.trainerId.name ||
          "Not assigned";
      }

      // Backend view sends full roster; detect via shape (isPresent on each row from API).
      const rosterFromApi =
        Array.isArray(record.userIds) &&
        record.userIds.length > 0 &&
        record.userIds.every(
          (u) =>
            u &&
            typeof u === "object" &&
            Object.prototype.hasOwnProperty.call(u, "isPresent") &&
            typeof u.isPresent === "boolean",
        );

      let sortedAttendanceArray;

      if (rosterFromApi) {
        const byName = [...record.userIds].sort((a, b) =>
          String(a?.name || "")
            .trim()
            .localeCompare(String(b?.name || "").trim(), undefined, {
              sensitivity: "base",
            }),
        );
        sortedAttendanceArray = byName.map((u, index) => ({
          srNo: index + 1,
          studentId: String(u.id || u._id || ""),
          studentData: {
            name: u.name || u.studentName || "Unknown Student",
            email: u.email || u.studentEmail || "N/A",
            phone: u.phone || u.mobileNumber || u.mobile || "N/A",
          },
          status: u.isPresent === true ? "present" : "absent",
        }));
        sortedAttendanceArray.sort((a, b) => {
          if (a.status === "present" && b.status === "absent") return -1;
          if (a.status === "absent" && b.status === "present") return 1;
          return 0;
        });
        sortedAttendanceArray = sortedAttendanceArray.map((row, index) => ({
          ...row,
          srNo: index + 1,
        }));
      } else {
        const studentMap = {};

        let batchStudentIdsStr =
          (batch && typeof batch.userId === "string" && batch.userId.trim()) ||
          "";

        if (
          !batchStudentIdsStr &&
          batch?.students &&
          Array.isArray(batch.students)
        ) {
          batchStudentIdsStr = batch.students
            .map((s) => (typeof s === "string" ? s : s.id || s._id))
            .join(",");
        } else if (
          !batchStudentIdsStr &&
          batch?.userIds &&
          Array.isArray(batch.userIds)
        ) {
          batchStudentIdsStr = batch.userIds
            .map((id) => (typeof id === "string" ? id : id.id || id._id))
            .join(",");
        } else if (
          !batchStudentIdsStr &&
          Array.isArray(record.batchId) &&
          record.batchId[0] &&
          typeof record.batchId[0].userId === "string"
        ) {
          batchStudentIdsStr = record.batchId[0].userId;
        } else if (
          !batchStudentIdsStr &&
          record.batchId &&
          typeof record.batchId === "string"
        ) {
          try {
            const { getBatchById } =
              await import("../../../../services/BatchService");
            const batchRes = await getBatchById(record.batchId);
            if (batchRes && batchRes.data && batchRes.data.userId) {
              batchStudentIdsStr = batchRes.data.userId;
              batch = batchRes.data;
            }
          } catch (e) {
            console.error(
              "Could not fetch batch details for student mapping",
              e,
            );
          }
        }

        if (batch?.students && Array.isArray(batch.students)) {
          batch.students.forEach((user) => {
            if (user && typeof user === "object") {
              const sid = String(user.id || user._id);
              studentMap[sid] = {
                name: user.studentName || user.name || "Unknown Student",
                email: user.studentEmail || user.email || "N/A",
                phone: user.phone || user.mobileNumber || user.mobile || "N/A",
              };
            }
          });
        } else if (batch?.userIds && Array.isArray(batch.userIds)) {
          batch.userIds.forEach((user) => {
            if (user && typeof user === "object") {
              const sid = String(user.id || user._id);
              studentMap[sid] = {
                name: user.studentName || user.name || "Unknown Student",
                email: user.studentEmail || user.email || "N/A",
                phone: user.phone || user.mobileNumber || user.mobile || "N/A",
              };
            }
          });
        }

        if (Array.isArray(record.userIds)) {
          record.userIds.forEach((u) => {
            if (u && typeof u === "object") {
              const sid = String(u.id || u._id);
              if (!sid) return;
              studentMap[sid] = {
                name: u.name || u.studentName || "Unknown Student",
                email: u.email || u.studentEmail || "N/A",
                phone: u.phone || u.mobileNumber || u.mobile || "N/A",
              };
            }
          });
        }

        let extractedPresentIds = [];

        if (Array.isArray(record.userIds)) {
          extractedPresentIds = record.userIds.map((u) =>
            typeof u === "string"
              ? u.trim()
              : String(u.id || u._id || "").trim(),
          );
        } else if (typeof record.userIds === "string") {
          extractedPresentIds = record.userIds
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
        } else if (Array.isArray(record.presentUserIds)) {
          extractedPresentIds = record.presentUserIds.map((u) =>
            typeof u === "string"
              ? u.trim()
              : String(u.id || u._id || "").trim(),
          );
        } else if (
          record.presentUserIds &&
          typeof record.presentUserIds === "string"
        ) {
          extractedPresentIds = record.presentUserIds
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
        } else if (record.students && Array.isArray(record.students)) {
          extractedPresentIds = record.students
            .filter((s) => s.status === "present")
            .map((s) => String(s.studentId || s.id || s._id).trim());
        } else if (record.rawUserIds) {
          if (Array.isArray(record.rawUserIds)) {
            extractedPresentIds = record.rawUserIds.map((u) =>
              typeof u === "string"
                ? u.trim()
                : String(u.id || u._id || "").trim(),
            );
          } else if (typeof record.rawUserIds === "string") {
            extractedPresentIds = record.rawUserIds
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean);
          }
        }

        const presentIdSet = new Set(
          extractedPresentIds
            .map((id) => String(id || "").trim())
            .filter(Boolean),
        );

        const allBatchUserIds = batchStudentIdsStr
          ? batchStudentIdsStr
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id)
          : [];

        let attendanceArray = allBatchUserIds.map((userId, index) => {
          const uid = String(userId || "").trim();
          const stuData =
            studentMap[userId] ||
            studentMap[uid] ||
            studentMap[String(userId)] ||
            null;

          return {
            srNo: index + 1,
            studentId: uid,
            studentData: stuData || {
              name: "Unknown Student",
              email: "N/A",
              phone: "N/A",
            },
            status: presentIdSet.has(uid) ? "present" : "absent",
          };
        });

        attendanceArray.sort((a, b) => {
          const nameA = a?.studentData?.name || "";
          const nameB = b?.studentData?.name || "";
          return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        });

        sortedAttendanceArray = attendanceArray.sort((a, b) => {
          if (a.status === "present" && b.status === "absent") return -1;
          if (a.status === "absent" && b.status === "present") return 1;
          return 0;
        });
      }

      const presentCount = sortedAttendanceArray.filter(
        (a) => a.status === "present",
      ).length;
      const absentCount = sortedAttendanceArray.filter(
        (a) => a.status === "absent",
      ).length;

      const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      };

      /* Whether this record can be edited is the API's answer, not one
         worked out again here: GET /attendances/:id returns `editability`
         for whoever asked -- an admin edits anything, a trainer only
         their own record and only within 48 hours of the session -- along
         with the sentence saying why not. The page adds the permission
         gate and prints the rest. Hiding Edit without a word was the
         whole complaint: the trainer could not tell a missing right from
         a closed window. */
      const editability = record.editability || null;
      const canEdit =
        hasPermission("add-attendance", "update", "lms") &&
        (editability ? editability.canEdit : true);
      /* The sentence, with the moment the window shut spelled out in the
         reader's own locale -- the API sends the instant, the page is
         what knows how to write a date here. */
      const lockedAt =
        editability?.windowEndsAt && new Date(editability.windowEndsAt);
      const editLockReason =
        editability && !editability.canEdit
          ? editability.code === "editWindowExpired" && lockedAt && !Number.isNaN(lockedAt.getTime())
            ? `${editability.reason} It closed on ${lockedAt.toLocaleString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}.`
            : editability.reason
          : null;
      // Deleting answers to the same rule the API applies to editing --
      // a record you may not change is not one you may remove either.
      const canDelete =
        hasPermission("list-attendance", "delete", "lms") &&
        (editability ? editability.canEdit : true);

      // Handed to the modal as data. This used to be a SweetAlert HTML
      // string with window.* callbacks hung off it; the student list is
      // searchable now, which is not something innerHTML does well.
      setDetail({
        id: recordId,
        record,
        batchName,
        courseName,
        trainerName,
        dateLabel: formatDate(record.date),
        topics: formatTopics(record.syllabusId),
        presentCount,
        absentCount,
        canEdit,
        editLockReason,
        canDelete,
        students: [...sortedAttendanceArray]
          .sort((a, b) =>
            (a.studentData?.name || "")
              .trim()
              .toLowerCase()
              .localeCompare((b.studentData?.name || "").trim().toLowerCase()),
          )
          .map((a) => ({
            id: a.studentData?.id || a.studentData?._id,
            name: a.studentData?.name || "",
            email: a.studentData?.email || "",
            phone: a.studentData?.phone || "",
            status: a.status,
          })),
      });
    } catch (err) {
      console.error("Error fetching attendance record for view:", err);
      toast.error("Failed to fetch attendance details");
    }
  };

  const headers = [
    {
      title: "Batch",
      prop: "batchName",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) => {
        const name = row.batchName || "";
        if (!name) return <span className="pg-none">—</span>;
        // Clipped. Batch names run long and pushed every column after them
        // off the row; the full value is on the tooltip.
        const clipped =
          name.length > BATCH_NAME_MAX
            ? `${name.slice(0, BATCH_NAME_MAX).trimEnd()}…`
            : name;
        return name === clipped ? (
          <span className="pg-strong">{name}</span>
        ) : (
          <Tip label={name}>
            <span className="pg-strong">{clipped}</span>
          </Tip>
        );
      },
    },
    {
      title: "Date",
      prop: "date",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) => {
        const formattedDate = new Date(row.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        return <span className="pg-date">{formattedDate}</span>;
      },
    },
    ...(hasPermission("list-trainer", "ie", "lms")
      ? [
          {
            title: "Trainer Name",
            prop: "trainerName",
            searchable: true,
            filterable: true,
            sortable: true,
            cell: (row) => <span>{row.trainerName || "—"}</span>,
          },
        ]
      : []),
    {
      title: "Course",
      prop: "courseName",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.courseName ? (
          <span className="pg-course" title={row.courseName}>
            {row.courseName}
          </span>
        ) : (
          <span className="pg-none">—</span>
        ),
    },
    {
      title: "Attendance Summary",
      prop: "presentUserIds",
      cell: (row) => {
        const presentCount = row.presentUserIds
          ? row.presentUserIds.length === 1 &&
            row.presentUserIds[0].toLowerCase() === "no students present"
            ? 0
            : row.presentUserIds.length
          : 0;

        let batch = null;
        if (Array.isArray(row.batchId) && row.batchId[0]) {
          batch = row.batchId[0];
        } else if (row.batchId && typeof row.batchId === "object") {
          batch = row.batchId;
        }

        let totalStudents = 0;
        if (batch?.userId) {
          totalStudents = batch.userId
            .split(",")
            .filter((id) => id.trim()).length;
        } else if (batch?.students && Array.isArray(batch.students)) {
          totalStudents = batch.students.length;
        } else if (batch?.userIds && Array.isArray(batch.userIds)) {
          totalStudents = batch.userIds.length;
        }

        const absentCount = Math.max(0, totalStudents - presentCount);

        return (
          <div className="pg-tally">
            <Tip label="Present">
              <span className="pg-pill pg-pill--good">P {presentCount}</span>
            </Tip>
            <Tip label="Absent">
              <span className="pg-pill pg-pill--bad">A {absentCount}</span>
            </Tip>
            <Tip label="Total enrolled">
              <span className="pg-pill">T {totalStudents}</span>
            </Tip>
          </div>
        );
      },
    },
  ];

  // The search box applies itself a beat after typing stops -- the one
  // control that does. The pickers still wait for Apply, because picking
  // three trainers should be one refetch, not three; typing is different,
  // you want to see the list narrow as you go.
  //
  // Skipped on the first render so mounting does not fire a second fetch
  // on top of the initial load.
  const searchPrimed = useRef(false);
  useEffect(() => {
    if (!searchPrimed.current) {
      searchPrimed.current = true;
      return undefined;
    }
    const t = setTimeout(() => {
      const term = searchDraft.trim();
      // Nothing to do if it already matches what the list was built from.
      if (term === searchTerm && searchFieldDraft === searchField) return;
      appendRef.current = false;
      setSearchTerm(term);
      setSearchField(searchFieldDraft);
      setCurrentPage(1);
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft, searchFieldDraft]);

  // Whether anything in the filter section is set -- what Clear has to undo.
  // Read from the drafts as well as the applied values, so a typed but
  // unapplied search still counts.
  const hasActiveFilters = Boolean(
    searchDraft ||
      searchTerm ||
      (searchFieldDraft && searchFieldDraft !== "all") ||
      selectedTrainer.length ||
      selectedBatch.length ||
      batchStatusFilter,
  );

  // Both roles get the same batch controls; only an admin also gets to pick a
  // trainer, so the two branches share one field list rather than duplicating it.
  const batchFilterField = (
    <div className="pg-field pg-field--grow">
      <label className="pg-label">Batch</label>
      <NewCustomAsyncSelectDropdown
        options={batchOptions}
        value={selectedBatch}
        onChange={handleBatchFilterSelect}
        placeholder="All batches"
        isMulti={true}
        isLoading={batchLoading}
        searchInControl={true}
        onMenuOpen={async () => {
          setBatchPage(1);
          setBatchSearch("");
          await fetchBatches(1, "", false);
        }}
        onInputChange={debounce(async (searchValue) => {
          setBatchSearch(searchValue);
          setBatchPage(1);
          await fetchBatches(1, searchValue, false);
        }, 500)}
        onMenuScrollToBottom={async () => {
          if (batchHasMore && !batchLoading) {
            const nextPage = batchPage + 1;
            await fetchBatches(nextPage, batchSearch, true);
          }
        }}
        onRefresh={handleBatchRefresh}
      />
    </div>
  );

  const batchStatusField = (
    <div className="pg-field pg-field--tight">
      <label className="pg-label">Batch status</label>
      <select
        className="pg-select"
        value={batchStatusFilter}
        onChange={(e) => handleBatchStatusChange(e.target.value)}
      >
        <option value="">All</option>
        <option value="active">Active</option>
        <option value="ended">Ended</option>
      </select>
    </div>
  );

  return (
    <div className="pg">

      <TopicsModal
        isOpen={topicsModal.isOpen}
        onClose={closeTopicsModal}
        topics={topicsModal.topics}
        title={topicsModal.title}
        date={topicsModal.date}
      />

      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="att-form-title"
          onClick={() => {
            setFormModal(null);
            setFormStep(null);
          }}
        >
          <div
            className="pg-modal-panel pg-modal-panel--xl pg-modal-panel--form"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">
                  {formModal.recordId ? "Edit attendance" : "Mark attendance"}
                </span>
                <h3 className="pg-modal-title" id="att-form-title">
                  {formStep?.title ||
                    (formModal.recordId
                      ? "Edit attendance"
                      : "Mark attendance")}
                </h3>
                <p className="pg-modal-sub">
                  {formStep?.hint ||
                    "Record who attended and what the session covered."}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {/* The same component the /add-attendance route renders --
                  embedded, so it drops its own header and reports back
                  here instead of navigating. */}
              {formDone ? (
                <PopupDone
                  message={formDone}
                  onClose={() => {
                    setFormModal(null);
                    setFormStep(null);
                  }}
                />
              ) : (
              <AddAttendance
                embedded
                recordId={formModal.recordId}
                onStepChange={(copy) => setFormStep(copy)}
                onCancel={() => setFormModal(null)}
                onDone={() => {
                  // Saved: the dialog shows the message in place of the form
                  // and closes itself a few seconds later.
                  // The record changed, so the view popup still open behind this form
                  // would be showing the old one. It goes; the form stays.
                  setDetail(null);
                  // Rebuild the list from page 1 so a new record appears
                  // and an edited one shows its new values.
                  appendRef.current = false;
                  if (currentPage === 1) {
                    if (currentUser) fetchAttendanceRecords(currentUser);
                  } else {
                    setCurrentPage(1);
                  }
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      <AttendanceDetailModal
        detail={detail}
        done={detailDone}
        onClose={() => setDetail(null)}
        onEdit={() => {
          const id = detail?.id;
          if (id) handleEdit(id);
        }}
        onDelete={() => {
          const id = detail?.id;
          if (id) handleDelete(id);
        }}
        onExport={() => detail?.record && handleExportRecord(detail.record)}
      />

      {/* Every control that narrows the list lives here, the search box
          included -- the table's own toolbar is switched off below so the
          two are not stacked one above the other. Nothing here takes
          effect until Apply, so picking three filters is one refetch. */}
      <FilterSheet label="Filter attendance">
        {/* Row one: the search box, the field it searches, and how many
            rows a load brings. The search box is half the row -- it was
            the full width, which is more than a batch name needs. */}
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="att-search">
              Search
            </label>
            <input
              id="att-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Search by batch, course or trainer name..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                runSearch(e.target.value, searchFieldDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="att-search-field">
              Search in
            </label>
            <select
              id="att-search-field"
              className="pg-select"
              value={searchFieldDraft}
              onChange={(e) => {
                setSearchFieldDraft(e.target.value);
                if (searchDraft.trim()) runSearch.flush(searchDraft, e.target.value);
              }}
            >
              <option value="all">All fields</option>
              <option value="batchName">Batch name</option>
              <option value="courseName">Course name</option>
              <option value="trainerName">Trainer name</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="att-rows">
              Rows per load
            </label>
            <select
              id="att-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Row two: the pickers, then the buttons. */}
        <div className="pg-filters-row">
          <div
            className={`pg-filters-fields${
              isAdmin ? "" : " pg-filters-fields--notrainer"
            }`}
          >
            {isAdmin && (
              <div className="pg-field">
                <label className="pg-label">Trainer</label>
                <NewCustomAsyncSelectDropdown
                  options={trainerOptions}
                  value={selectedTrainer}
                  onChange={handleTrainerFilterSelect}
                  placeholder="All trainers"
                  isMulti={true}
                  isLoading={trainerLoading}
                  searchInControl={true}
                  onMenuOpen={async () => {
                    setTrainerPage(1);
                    setTrainerSearch("");
                    await fetchTrainers(1, "", false);
                  }}
                  onInputChange={debounce(async (searchValue) => {
                    setTrainerSearch(searchValue);
                    setTrainerPage(1);
                    await fetchTrainers(1, searchValue, false);
                  }, 500)}
                  onMenuScrollToBottom={async () => {
                    if (trainerHasMore && !trainerLoading) {
                      const nextPage = trainerPage + 1;
                      await fetchTrainers(nextPage, trainerSearch, true);
                    }
                  }}
                  onRefresh={handleTrainerRefresh}
                />
              </div>
            )}

            {batchFilterField}
            {batchStatusField}

          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>

            <span className="pg-filters-sep" aria-hidden="true" />

            <button
              type="button"
              className="pg-btn"
              onClick={() => setExportOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={exportOpen}
            >
              <Download />
              Export
            </button>
            {hasPermission("add-attendance", "add", "lms") && (
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setFormModal({ recordId: null })}
              >
                Mark attendance
              </button>
            )}
          </div>
        </div>
      </FilterSheet>

      {/* --attpage, not --attendance: the dashboard's attendance tab already
          uses td-list--attendance for its own hand-written table, and these
          rules are unscoped. Same prefix collision td-bar/td-bars hit. */}
      <div className="td-list td-list--attpage" ref={listRef}>
        <NewCustomDataTable
          data={attendanceRecords}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => handleView(row.id)}
          // Every cell wraps its text in a span, so the default
          // ['TR', 'TD'] meant clicking the text itself did nothing and
          // only the padding around it opened the record. Nothing inside
          // a row handles its own click any more, so all of it counts.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: "button",
            "aria-label": `Open attendance for ${row.batchName || "this batch"}`,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleView(row.id);
              }
            },
          })}
        />
      </div>

      {/* What the page buttons used to say, said once: how far down the
          list the reader is. The sentinel below it is what the observer
          watches -- crossing it fetches the next batch. */}
      <div className="pg-scroll-foot" ref={listEndRef}>
        {attendanceRecords.length < totalRecords ? (
          <>
            <span className="pg-scroll-count">
              {attendanceRecords.length} of {totalRecords}
            </span>
            {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
          </>
        ) : (
          totalRecords > 0 && (
            <span className="pg-scroll-count">
              All {totalRecords} record{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {/* The view popup's Export. It sits ABOVE that popup rather than
          replacing it (xp- is z-index 1090 to pg-modal's 1080), so the
          reader comes back to the record they were reading. */}
      <ExportDialog
        open={Boolean(recordExport)}
        onClose={() => setRecordExport(null)}
        title="Attendance record"
        columns={recordExportColumns}
        rows={recordExport?.rows || []}
        preamble={recordExport?.preamble}
        filenameBase={recordExport?.filenameBase || "attendance-record"}
        scopeNote={recordExport?.scopeNote}
      />

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Attendance summary"
        columns={summaryExportColumns}
        rows={getFilteredData()}
        filenameBase="attendance-summary"
        scopeNote={
          attendanceRecords.length < totalRecords
            ? `${attendanceRecords.length} of ${totalRecords} records are loaded — scroll the list to bring in the rest first.`
            : searchTerm
              ? `Matching “${searchTerm}”.`
              : undefined
        }
      />
    </div>
  );
};

export default ListAttendance;
